import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, from, map, switchMap, tap, filter, take } from 'rxjs';
import { NormsCacheService } from './norms-cache.service';
import { AuthService } from './auth.service';
import { environment } from '@env/environment';
import {
  NormsLookupRequest,
  NormsLookupResponse,
  LaborHoursRequest,
  LaborHoursResponse,
  PipeFabRatesRequest,
  PipeFabRatesResponse,
  NormsTask,
  DashboardMetrics,
  Craft
} from '../models/norms.model';

/** Payload written to the trusted metrics log (dbo.EstimateLog) when a planner
 *  finalizes an estimate by copying its reference code. */
export interface EstimateLogPayload {
  referenceCode: string;
  taskId?: string;
  taskName?: string;
  craft?: string;
  crewSize?: number | null;
  baseHours?: number;
  multiplierProduct?: number;
  adjustedHours?: number;
  rawAdjustedHours?: number;
  hoursPerResource?: number | null;
  rawHoursPerResource?: number | null;
  maximoWoNumber?: string | null;
}

/** One row returned by GET /api/estimate-log/recent (dbo.EstimateLog). */
export interface EstimateLogRow {
  id: number;
  referenceCode: string;
  taskId?: string;
  taskName?: string;
  craft?: string;
  crewSize?: number | null;
  baseHours?: number;
  multiplierProduct?: number;
  adjustedHours?: number;
  rawAdjustedHours?: number;
  hoursPerResource?: number | null;
  rawHoursPerResource?: number | null;
  maximoWoNumber?: string | null;
  userEmail?: string;
  env?: string;
  generatedAt: string;
}

/** Query params for the paged/searchable recent-estimates read. */
export interface RecentEstimatesQuery {
  page?: number;
  pageSize?: number;
  from?: string;   // ISO datetime lower bound (date-window mode)
  to?: string;     // ISO datetime upper bound (date-window mode)
  wo?: string;     // WO# search — all-time, ignores the date window
}

/** One page of recent estimates plus the full-query total (for the pager). */
export interface RecentEstimatesResult {
  rows: EstimateLogRow[];
  total: number;
  page: number;
  pageSize: number;
}

/** One weekly NORMs-adoption row returned by GET /api/norms-adoption
 *  (dbo.NormsAdoptionWeekly). adoptionPct = normalizedTasks ÷ totalTasksScheduled. */
export interface NormsAdoptionRow {
  weekStartMonday: string;
  siteId: string;
  totalTasksScheduled: number;
  normalizedTasks: number;
  adoptionPct: number;
  snapshotDate: string;
}

/** Per-craft breakdown row from GET /api/norms-adoption-craft
 *  (dbo.NormsAdoptionCraftWeekly). Independent per-craft lens — counts do NOT
 *  sum back to the site totals in NormsAdoptionRow. */
export interface NormsAdoptionCraftRow {
  weekStartMonday: string;
  siteId: string;
  craft: string;
  totalTasksScheduled: number;
  normalizedTasks: number;
  adoptionPct: number;
  snapshotDate: string;
}

/** Per-area breakdown row from GET /api/norms-adoption-area
 *  (dbo.NormsAdoptionAreaWeekly). Independent per-area lens — counts do NOT
 *  sum back to the site totals in NormsAdoptionRow. */
export interface NormsAdoptionAreaRow {
  weekStartMonday: string;
  siteId: string;
  area: string;
  totalTasksScheduled: number;
  normalizedTasks: number;
  adoptionPct: number;
  snapshotDate: string;
}

/** Payload written to dbo.Feedback when a planner submits a NORM request / feedback. */
export interface FeedbackPayload {
  feedbackType: 'General' | 'Bug' | 'FeatureRequest' | 'NormModification' | 'NormAddition';
  area?: string | null;
  rating?: number | null;
  referenceCode?: string | null;
  taskId?: string | null;
  taskName?: string | null;
  craft?: string | null;
  site?: string | null;
  currentHours?: number | null;
  suggestedHours?: number | null;
  changeType?: string | null;
  suggestedHeadcount?: number | null;
  suggestedDurationHours?: number | null;
  scopeOfWork?: string | null;
  typicalScope?: boolean | null;
  evidence?: string | null;
  jobsReviewed?: number | null;
  avgActualHours?: number | null;
  avgActualCrewSize?: number | null;
  frequency?: string | null;
  multiSite?: string | null;
  message?: string | null;
  detailsJson?: string | null;
}

/** One row returned by GET /api/feedback (dbo.Feedback). */
export interface FeedbackRow {
  Id: number;
  FeedbackType: string;
  Area?: string | null;
  Rating?: number | null;
  ReferenceCode?: string | null;
  TaskId?: string | null;
  TaskName?: string | null;
  Craft?: string | null;
  Site?: string | null;
  CurrentHours?: number | null;
  SuggestedHours?: number | null;
  ChangeType?: string | null;
  SuggestedHeadcount?: number | null;
  SuggestedDurationHours?: number | null;
  ScopeOfWork?: string | null;
  TypicalScope?: boolean | null;
  Evidence?: string | null;
  JobsReviewed?: number | null;
  AvgActualHours?: number | null;
  AvgActualCrewSize?: number | null;
  Frequency?: string | null;
  MultiSite?: string | null;
  Message?: string | null;
  UserEmail?: string | null;
  Env?: string | null;
  Status?: string | null;
  SubmittedAt: string;
}

/** Query params for the paged/filterable feedback report. */
export interface FeedbackQuery {
  page?: number;
  pageSize?: number;
  type?: string;    // FeedbackType exact match
  status?: string;  // Status exact match
  from?: string;    // ISO datetime lower bound on SubmittedAt
  to?: string;      // ISO datetime upper bound on SubmittedAt
  q?: string;       // free-text across taskName/ref/message/scope/user
}

/** One page of feedback rows plus the full-query total (for the pager). */
export interface FeedbackResult {
  rows: FeedbackRow[];
  total: number;
  page: number;
  pageSize: number;
}

@Injectable({
  providedIn: 'root'
})
export class NormsService {
  private http = inject(HttpClient);
  private apiUrl = environment.nestApiUrl;
  private normsCacheService = inject(NormsCacheService);
  private authService = inject(AuthService);

  /** Lookup NORMs task by query string */
  lookupNormsTask(taskQuery: string): Observable<NormsLookupResponse> {
    const request: NormsLookupRequest = {
      lookup_type: 'norms',
      task_query: taskQuery
    };
    const cacheKey = JSON.stringify(request);

    return from(this.normsCacheService.get(cacheKey)).pipe(
      switchMap(cached => {
        if (cached) return of(cached as NormsLookupResponse);
        return this.http.post<NormsLookupResponse>(this.apiUrl, request, {
          headers: this.getHeaders()
        }).pipe(
          // ML endpoint may return double-serialized JSON (a JSON string); unwrap if needed
          map(response =>
            (typeof response === 'string' ? JSON.parse(response) : response) as NormsLookupResponse
          ),
          tap(response => {
            if (!('error' in response)) {
              this.normsCacheService.set(cacheKey, response);
            }
          })
        );
      })
    );
  }

  /** Lookup labor hours using RAG (generic or hierarchical) */
  lookupLaborHours(request: LaborHoursRequest): Observable<LaborHoursResponse> {
    const cacheKey = JSON.stringify(request);

    return from(this.normsCacheService.get(cacheKey)).pipe(
      switchMap(cached => {
        if (cached) return of(cached as LaborHoursResponse);
        return this.http.post<LaborHoursResponse>(this.apiUrl, request, {
          headers: this.getHeaders()
        }).pipe(
          map(response =>
            (typeof response === 'string' ? JSON.parse(response) : response) as LaborHoursResponse
          ),
          tap(response => {
            if (!('error' in response)) {
              this.normsCacheService.set(cacheKey, response);
            }
          })
        );
      })
    );
  }

  /** Fetch the full Pipe Fabrication rate book (size × schedule/rating × location matrix).
   *  Cached in IndexedDB (fixed key, since the request has no params). Invalidation is tied
   *  to the backend: norms-meta advertises the current pipe-fab contract version, and a
   *  cached book whose version differs is ignored and refetched — so a backend contract bump
   *  (e.g. the dedup fix) refreshes every client with no code change, just an env bump. */
  lookupPipeFabRates(): Observable<PipeFabRatesResponse> {
    const request: PipeFabRatesRequest = { lookup_type: 'pipe_fab_rates' };
    const cacheKey = JSON.stringify(request);

    return from(this.normsCacheService.ready).pipe(
      switchMap(() => from(this.normsCacheService.get(cacheKey))),
      switchMap(cached => {
        const book = cached as PipeFabRatesResponse | null;
        const expected = this.normsCacheService.meta?.pipeFabVersion;
        // Honour cache when it matches the backend-advertised version, or (if meta is
        // unavailable) fall back to any cached book rather than forcing a refetch.
        if (book && (!expected || book.version === expected)) return of(book);
        return this.http.post<PipeFabRatesResponse>(this.apiUrl, request, {
          headers: this.getHeaders()
        }).pipe(
          // ML endpoint may return double-serialized JSON (a JSON string); unwrap if needed
          map(response =>
            (typeof response === 'string' ? JSON.parse(response) : response) as PipeFabRatesResponse
          ),
          tap(response => {
            if (!response.error) {
              this.normsCacheService.set(cacheKey, response);
            }
          })
        );
      })
    );
  }

  // ── Demo readiness ──────────────────────────────────────────────────────
  private warmed = false;

  /**
   * Pre-warm the Function proxy + Azure ML endpoint so the FIRST real lookup in
   * a demo is fast (cold starts on the Consumption plan can add several seconds).
   * Fire-and-forget; safe to call repeatedly (no-ops after the first call).
   */
  prewarm(): void {
    if (this.warmed) return;
    this.warmed = true;
    // Wait until a bearer token is actually available before firing the warmup,
    // otherwise the very first call races auth init and returns a guaranteed 401.
    // The token is still validated server-side on every request — this only avoids
    // a known-useless request while MSAL is still acquiring the token.
    this.authService.getApiToken().pipe(
      filter(token => !!token),
      take(1),
      switchMap(() => this.lookupNormsTask('__warmup__'))
    ).subscribe({
      next: () => {},
      error: () => { /* warmup failures are non-fatal */ }
    });
  }

  // ── KPI / actuals data hooks (STUBS — full dashboard is a later phase) ────
  private normalizedJobs: Array<{ referenceCode: string; taskId: string; taskName: string; adjustedHours: number; at: string }> = [];
  private actualsVariance: Array<{ taskId: string; normHours: number; actualHours: number; variance: number; at: string }> = [];

  /** Stub: mark a job as normalized (captured for future KPI dashboard). */
  recordNormalizedJob(referenceCode: string, taskId: string, taskName: string, adjustedHours: number): void {
    this.normalizedJobs.push({ referenceCode, taskId, taskName, adjustedHours, at: new Date().toISOString() });
  }

  /** Returns all jobs normalized in this session (newest first). */
  getNormalizedJobs(): Array<{ referenceCode: string; taskId: string; taskName: string; adjustedHours: number; at: string }> {
    return [...this.normalizedJobs].reverse();
  }

  /** Stub: capture actuals-vs-norms variance for a completed job. */
  captureActualVsNorm(taskId: string, normHours: number, actualHours: number): void {
    this.actualsVariance.push({
      taskId, normHours, actualHours,
      variance: Math.round((actualHours - normHours) * 100) / 100,
      at: new Date().toISOString(),
    });
  }

  /** Get all NORMs tasks for the data table (cached list) */
  getAllNormsTasks(): Observable<NormsTask[]> {
    // In a real implementation, this would call an API endpoint that returns the full list
    // For now, we'll use mock data that matches the CSV structure
    return of(this.getMockNormsTasks());
  }

  /** Calculate dashboard metrics from tasks */
  calculateMetrics(tasks: NormsTask[]): DashboardMetrics {
    const totalTasks = tasks.length;
    const tasksWithHours = tasks.filter(t => t.normHours > 0).length;
    const zeroHourTasks = tasks.filter(t => t.normHours === 0).length;
    const validTasks = tasks.filter(t => t.normHours > 0);
    const avgNormHours = validTasks.length > 0
      ? validTasks.reduce((sum, t) => sum + t.normHours, 0) / validTasks.length
      : 0;

    const craftBreakdown = tasks.reduce((acc, task) => {
      acc[task.craft] = (acc[task.craft] || 0) + 1;
      return acc;
    }, {} as Record<Craft, number>);

    return {
      totalTasks,
      tasksWithHours,
      zeroHourTasks,
      avgNormHours: Math.round(avgNormHours * 10) / 10,
      craftBreakdown
    };
  }

  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    if (environment.nestApiKeyHeader && environment.nestApiKeyValue) {
      headers = headers.set(environment.nestApiKeyHeader, environment.nestApiKeyValue);
    }

    return headers;
  }

  /**
   * Fire-and-forget: record a finalized estimate to the trusted metrics log
   * (Azure SQL via the estimate-log Function). Called when a planner copies the
   * reference code. The app-insights interceptor attaches the bearer token to
   * this /api/ call so the Function can stamp the verified user. Failures are
   * swallowed — metrics logging must never disrupt the planner's flow.
   */
  logEstimate(payload: EstimateLogPayload): void {
    const url = this.apiUrl.replace(/nest-lookup$/, 'estimate-log');
    this.http.post(url, payload, { headers: this.getHeaders() }).subscribe({
      next: () => {},
      error: () => { /* metrics logging is non-fatal */ }
    });
  }

  /**
   * Read one page of finalized estimates from the trusted metrics log
   * (GET /api/estimate-log/recent). Supports a date window (from/to) or an
   * all-time WO# search (wo), plus server-side paging. The interceptor attaches
   * the bearer token. Returns the page rows and the full-query total.
   */
  getRecentEstimates(query: RecentEstimatesQuery = {}): Observable<RecentEstimatesResult> {
    const url = this.apiUrl.replace(/nest-lookup$/, 'estimate-log/recent');
    const params: Record<string, string> = {};
    if (query.page != null) params['page'] = String(query.page);
    if (query.pageSize != null) params['pageSize'] = String(query.pageSize);
    if (query.from) params['from'] = query.from;
    if (query.to) params['to'] = query.to;
    if (query.wo) params['wo'] = query.wo;
    return this.http
      .get<{ ok: boolean; page: number; pageSize: number; total: number; count: number; rows: EstimateLogRow[] }>(url, {
        headers: this.getHeaders(),
        params,
      })
      .pipe(map(res => ({
        rows: res?.rows ?? [],
        total: res?.total ?? 0,
        page: res?.page ?? (query.page ?? 0),
        pageSize: res?.pageSize ?? (query.pageSize ?? 10),
      })));
  }

  /**
   * Read the pre-aggregated weekly NORMs adoption (GET /api/norms-adoption) for
   * the Dashboard tile. `weeks` bounds the window; `site` (one of the 5 site ids)
   * drills in, omitted = every site. The interceptor attaches the bearer token.
   */
  getNormsAdoption(weeks = 14, site?: string): Observable<NormsAdoptionRow[]> {
    const url = this.apiUrl.replace(/nest-lookup$/, 'norms-adoption');
    const params: Record<string, string> = { weeks: String(weeks) };
    if (site && site !== 'all') params['site'] = site;
    return this.http
      .get<{ ok: boolean; rows: NormsAdoptionRow[] }>(url, { headers: this.getHeaders(), params })
      .pipe(map(res => res?.rows ?? []));
  }

  /**
   * Read the per-craft adoption breakdown for one week (GET /api/norms-adoption-craft),
   * powering the drill-down modal. `week` is a yyyy-mm-dd Monday; `site` optionally
   * narrows to one site. Counts are an independent per-craft lens.
   */
  getNormsAdoptionCraft(week: string, site?: string): Observable<NormsAdoptionCraftRow[]> {
    const url = this.apiUrl.replace(/nest-lookup$/, 'norms-adoption-craft');
    const params: Record<string, string> = { week };
    if (site && site !== 'all') params['site'] = site;
    return this.http
      .get<{ ok: boolean; rows: NormsAdoptionCraftRow[] }>(url, { headers: this.getHeaders(), params })
      .pipe(map(res => res?.rows ?? []));
  }

  /**
   * Read the per-area adoption breakdown for one week (GET /api/norms-adoption-area),
   * the area companion to getNormsAdoptionCraft. `week` is a yyyy-mm-dd Monday;
   * `site` optionally narrows to one site. Counts are an independent per-area lens.
   */
  getNormsAdoptionArea(week: string, site?: string): Observable<NormsAdoptionAreaRow[]> {
    const url = this.apiUrl.replace(/nest-lookup$/, 'norms-adoption-area');
    const params: Record<string, string> = { week };
    if (site && site !== 'all') params['site'] = site;
    return this.http
      .get<{ ok: boolean; rows: NormsAdoptionAreaRow[] }>(url, { headers: this.getHeaders(), params })
      .pipe(map(res => res?.rows ?? []));
  }

  /**
   * Persist a feedback / NORM request to dbo.Feedback (POST /api/feedback).
   * The interceptor attaches the bearer token so the Function stamps the
   * verified user. Returns the observable so the caller can show success/failure.
   */
  submitFeedback(payload: FeedbackPayload): Observable<{ ok: boolean }> {
    const url = this.apiUrl.replace(/nest-lookup$/, 'feedback');
    return this.http.post<{ ok: boolean }>(url, payload, { headers: this.getHeaders() });
  }

  /**
   * Read one page of submitted feedback for the in-app report
   * (GET /api/feedback). Supports type/status/date/free-text filters and
   * server-side paging. Returns the page rows and the full-query total.
   */
  getFeedback(query: FeedbackQuery = {}): Observable<FeedbackResult> {
    const url = this.apiUrl.replace(/nest-lookup$/, 'feedback');
    const params: Record<string, string> = {};
    if (query.page != null) params['page'] = String(query.page);
    if (query.pageSize != null) params['pageSize'] = String(query.pageSize);
    if (query.type) params['type'] = query.type;
    if (query.status) params['status'] = query.status;
    if (query.from) params['from'] = query.from;
    if (query.to) params['to'] = query.to;
    if (query.q) params['q'] = query.q;
    return this.http
      .get<{ ok: boolean; page: number; pageSize: number; total: number; count: number; rows: FeedbackRow[] }>(url, {
        headers: this.getHeaders(),
        params,
      })
      .pipe(map(res => ({
        rows: res?.rows ?? [],
        total: res?.total ?? 0,
        page: res?.page ?? (query.page ?? 0),
        pageSize: res?.pageSize ?? (query.pageSize ?? 20),
      })));
  }

  /** Mock data for development - matches your NORMs CSV structure */
  private getMockNormsTasks(): NormsTask[] {
    return [
      // INST - Transmitters (all zero hours - pending SME validation)
      { task: 'Impulse Flow Transmitter PM', craft: 'INST', loe: 'S', resourceCount: 2, normHours: 0, hoursPending: true, embeddedTasks: '', activityBreakdown: this.emptyBreakdown() },
      { task: 'Inline Flow Transmitter PM', craft: 'INST', loe: 'S', resourceCount: 2, normHours: 0, hoursPending: true, embeddedTasks: '', activityBreakdown: this.emptyBreakdown() },
      { task: 'Radar Level Transmitter PM', craft: 'INST', loe: 'S', resourceCount: 2, normHours: 0, hoursPending: true, embeddedTasks: '', activityBreakdown: this.emptyBreakdown() },
      { task: 'Nuclear Level Transmitter PM', craft: 'INST', loe: 'M', resourceCount: 2, normHours: 0, hoursPending: true, embeddedTasks: '', activityBreakdown: this.emptyBreakdown() },
      { task: 'Temperature Transmitter PM', craft: 'INST', loe: 'S', resourceCount: 1, normHours: 0, hoursPending: true, embeddedTasks: '', activityBreakdown: this.emptyBreakdown() },
      
      // INST - Valves (with hours)
      { task: 'Control Valve PM - Small', craft: 'INST', loe: 'S', resourceCount: 2, normHours: 4.5, hoursPending: false, embeddedTasks: 'Inspect, Clean, Test', activityBreakdown: { ...this.emptyBreakdown(), inspection: true } },
      { task: 'Control Valve PM - Medium', craft: 'INST', loe: 'M', resourceCount: 2, normHours: 6.0, hoursPending: false, embeddedTasks: 'Inspect, Clean, Test', activityBreakdown: { ...this.emptyBreakdown(), inspection: true } },
      { task: 'Control Valve PM - Large', craft: 'INST', loe: 'L', resourceCount: 3, normHours: 8.0, hoursPending: false, embeddedTasks: 'Inspect, Clean, Test', activityBreakdown: { ...this.emptyBreakdown(), inspection: true } },
      { task: 'Safety Relief Valve Test', craft: 'INST', loe: 'S', resourceCount: 2, normHours: 3.0, hoursPending: false, embeddedTasks: 'Test, Document', activityBreakdown: { ...this.emptyBreakdown(), inspection: true } },
      
      // MACH - Pumps
      { task: 'Centrifugal Pump PM - Small', craft: 'MACH', loe: 'S', resourceCount: 2, normHours: 4.0, hoursPending: false, embeddedTasks: 'Vibration, Alignment', activityBreakdown: { ...this.emptyBreakdown(), alignment: true, lubrication: true } },
      { task: 'Centrifugal Pump PM - Medium', craft: 'MACH', loe: 'M', resourceCount: 2, normHours: 6.0, hoursPending: false, embeddedTasks: 'Vibration, Alignment', activityBreakdown: { ...this.emptyBreakdown(), alignment: true, lubrication: true } },
      { task: 'Centrifugal Pump PM - Large', craft: 'MACH', loe: 'L', resourceCount: 3, normHours: 10.0, hoursPending: false, embeddedTasks: 'Vibration, Alignment', activityBreakdown: { ...this.emptyBreakdown(), alignment: true, lubrication: true } },
      { task: 'Pump Removal - Small', craft: 'MACH', loe: 'S', resourceCount: 2, normHours: 0, hoursPending: true, embeddedTasks: 'Disconnect, Remove', activityBreakdown: { ...this.emptyBreakdown(), removeEquipment: true } },
      { task: 'Pump Install - Small', craft: 'MACH', loe: 'S', resourceCount: 2, normHours: 0, hoursPending: true, embeddedTasks: 'Install, Align', activityBreakdown: { ...this.emptyBreakdown(), installEquipment: true, alignment: true } },
      
      // MACH - Compressors
      { task: 'Reciprocating Compressor PM', craft: 'MACH', loe: 'L', resourceCount: 3, normHours: 16.0, hoursPending: false, embeddedTasks: 'Inspect valves, Bearings', activityBreakdown: { ...this.emptyBreakdown(), inspection: true, lubrication: true } },
      { task: 'Screw Compressor PM', craft: 'MACH', loe: 'M', resourceCount: 2, normHours: 8.0, hoursPending: false, embeddedTasks: 'Oil analysis, Vibration', activityBreakdown: { ...this.emptyBreakdown(), inspection: true, lubrication: true } },
      
      // ELEC
      { task: 'Motor PM - Small', craft: 'ELEC', loe: 'S', resourceCount: 1, normHours: 2.0, hoursPending: false, embeddedTasks: 'Meg, Vibration', activityBreakdown: { ...this.emptyBreakdown(), inspection: true } },
      { task: 'Motor PM - Medium', craft: 'ELEC', loe: 'M', resourceCount: 2, normHours: 4.0, hoursPending: false, embeddedTasks: 'Meg, Vibration, Thermal', activityBreakdown: { ...this.emptyBreakdown(), inspection: true } },
      { task: 'Motor PM - Large', craft: 'ELEC', loe: 'L', resourceCount: 2, normHours: 6.0, hoursPending: false, embeddedTasks: 'Meg, Vibration, Thermal', activityBreakdown: { ...this.emptyBreakdown(), inspection: true } },
      { task: 'UPS Battery Test', craft: 'ELEC', loe: 'S', resourceCount: 1, normHours: 0, hoursPending: true, embeddedTasks: 'Load test, Inspect', activityBreakdown: { ...this.emptyBreakdown(), inspection: true } },
      { task: 'Breaker PM', craft: 'ELEC', loe: 'M', resourceCount: 2, normHours: 0, hoursPending: true, embeddedTasks: 'Trip test, Clean', activityBreakdown: { ...this.emptyBreakdown(), inspection: true } },
      
      // PFBM - Pipe Fab
      { task: 'Flange Makeup - 2 inch', craft: 'PFBM', loe: 'S', resourceCount: 2, normHours: 1.5, hoursPending: false, embeddedTasks: '', activityBreakdown: { ...this.emptyBreakdown(), makeupFlangeInlet: true } },
      { task: 'Flange Makeup - 4 inch', craft: 'PFBM', loe: 'S', resourceCount: 2, normHours: 2.0, hoursPending: false, embeddedTasks: '', activityBreakdown: { ...this.emptyBreakdown(), makeupFlangeInlet: true } },
      { task: 'Flange Makeup - 6 inch', craft: 'PFBM', loe: 'M', resourceCount: 2, normHours: 2.5, hoursPending: false, embeddedTasks: '', activityBreakdown: { ...this.emptyBreakdown(), makeupFlangeInlet: true } },
      { task: 'Flange Break - 2 inch', craft: 'PFBM', loe: 'S', resourceCount: 2, normHours: 1.0, hoursPending: false, embeddedTasks: '', activityBreakdown: { ...this.emptyBreakdown(), breakFlangeInlet: true } },
      { task: 'Blind Installation - 4 inch', craft: 'PFBM', loe: 'S', resourceCount: 2, normHours: 1.5, hoursPending: false, embeddedTasks: '', activityBreakdown: { ...this.emptyBreakdown(), installBlankInlet: true } },
    ];
  }

  private emptyBreakdown() {
    return {
      breakFlangeInlet: false,
      breakFlangeOutlet: false,
      makeupFlangeInlet: false,
      makeupFlangeOutlet: false,
      removeEquipment: false,
      installEquipment: false,
      installBlankInlet: false,
      installBlankOutlet: false,
      removeBlankInlet: false,
      removeBlankOutlet: false,
      alignment: false,
      lubrication: false,
      inspection: false,
      other: false
    };
  }
}
